import React, { useState } from 'react';
import {
  Alert,
  Button,
  Content,
  Form,
  FormGroup,
  FormHelperText,
  HelperText,
  HelperTextItem,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  TextInput,
} from '@patternfly/react-core';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useAdminGpuActions } from '../../../hooks/useAdminVerdaServers';
import type { VerdaGpuInvoice } from '../../../types/models/Verda';
import { formatMonthYear } from '../../../utils/dateFormat';
import { NoteField, TypedConfirmField, isValidNote, typedMatches } from './VerdaGpuParts';

export type VerdaInvoiceAction = 'link' | 'release' | 'close';

/** What an invoice action changed: an updated row (link, close) or a deleted one (release). */
export type VerdaInvoiceActionResult = { kind: 'updated'; invoice: VerdaGpuInvoice } | { kind: 'released'; id: number };

interface VerdaInvoiceActionModalProps {
  action: VerdaInvoiceAction;
  invoice: VerdaGpuInvoice;
  onClose: () => void;
  onDone: (result: VerdaInvoiceActionResult) => void;
}

const TITLES: Record<VerdaInvoiceAction, string> = {
  link: 'Link a Facturier invoice',
  release: 'Release GPU invoice',
  close: 'Close GPU invoice',
};

const POSITIVE_INT_RE = /^[1-9]\d{0,9}$/;

/**
 * The staff actions on a GPU invoice (docs/api-types/admin-verda.md "Monthly consumption invoices"):
 * - Link: record the Facturier invoice an ambiguous create produced (checked live against the totals
 *   and the search marker), then mark it paid.
 * - Release: DELETE the row so the month is invoiced again — only once Facturier provably has no
 *   invoice for the marker, or the customer is invoiced twice. Typed search-marker confirmation.
 * - Close: terminal — the hours are never invoiced again (a credit note or manual invoice was made).
 * Every outcome notifies through the drawer; a refusal is also shown inline, verbatim.
 * Mounted only while open (keyed upstream), so each run starts empty.
 */
function VerdaInvoiceActionModal({ action, invoice, onClose, onDone }: VerdaInvoiceActionModalProps) {
  const { linkInvoice, releaseInvoice, closeInvoice, busy } = useAdminGpuActions();
  const { addNotification, setDrawerOpen } = useNotifications();
  const [facturierId, setFacturierId] = useState('');
  const [typedMarker, setTypedMarker] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const label = `GPU invoice #${invoice.id} (${invoice.search_marker})`;
  const facturierIdValid = POSITIVE_INT_RE.test(facturierId.trim());
  const canSubmit =
    !busy &&
    (action === 'link'
      ? facturierIdValid
      : action === 'release'
        ? typedMatches(typedMarker, invoice.search_marker) && isValidNote(note)
        : isValidNote(note));

  const notify = (variant: 'success' | 'warning' | 'danger', title: string, description: string) => {
    addNotification({
      title,
      srTitle: variant === 'danger' ? 'Error alert' : variant === 'warning' ? 'Warning alert' : 'Success alert',
      description,
      variant,
    });
    setDrawerOpen(true);
  };

  const fail = (message: string, errors: Record<string, string>) => {
    setError(message);
    setFieldErrors(errors);
    notify('danger', `Could not ${action} ${label}`, message);
  };

  const handleSubmit = async () => {
    setError(null);
    setFieldErrors({});
    if (action === 'link') {
      const outcome = await linkInvoice(invoice.id, Number(facturierId.trim()));
      if (outcome.status === 'error') {
        fail(outcome.message, outcome.fieldErrors);
        return;
      }
      const linked = outcome.data.invoice;
      if (linked.status === 'paid') {
        notify(
          'success',
          'Invoice linked',
          `${label} is linked to Facturier invoice ${facturierId.trim()} and marked paid.`,
        );
      } else {
        notify(
          'warning',
          'Invoice linked, not marked paid yet',
          `${label} is linked to Facturier invoice ${facturierId.trim()}; the mark-paid step failed and the invoicer retries it.`,
        );
      }
      onDone({ kind: 'updated', invoice: linked });
      onClose();
      return;
    }
    if (action === 'release') {
      const outcome = await releaseInvoice(invoice.id, note.trim());
      if (outcome.status === 'error') {
        fail(outcome.message, outcome.fieldErrors);
        return;
      }
      notify('success', 'Invoice released', `${label} was deleted; its month is invoiced again at the next run.`);
      onDone({ kind: 'released', id: outcome.data.released });
      onClose();
      return;
    }
    const outcome = await closeInvoice(invoice.id, note.trim());
    if (outcome.status === 'error') {
      fail(outcome.message, outcome.fieldErrors);
      return;
    }
    notify('success', 'Invoice closed', `${label} is closed: its hours are never invoiced again.`);
    onDone({ kind: 'updated', invoice: outcome.data.invoice });
    onClose();
  };

  const renderBody = () => {
    switch (action) {
      case 'link':
        return (
          <>
            <Content component="p">
              Search Facturier for <strong className="pf-v6-u-font-family-monospace">{invoice.search_marker}</strong> (
              {invoice.client.name ?? `client #${invoice.client.id}`}, {formatMonthYear(invoice.period)}). The invoice
              you enter is read live and must belong to this client, carry the same totals and name the marker; it is
              then recorded and marked paid.
            </Content>
            <Form onSubmit={(event) => event.preventDefault()}>
              <FormGroup label="Facturier invoice id" fieldId="verda-invoice-link-id" isRequired>
                <TextInput
                  id="verda-invoice-link-id"
                  name="verda-invoice-link-id"
                  type="text"
                  inputMode="numeric"
                  value={facturierId}
                  onChange={(_event, value) => setFacturierId(value)}
                  validated={
                    fieldErrors.facturier_invoice_id || (facturierId !== '' && !facturierIdValid) ? 'error' : 'default'
                  }
                  isRequired
                  autoComplete="off"
                  aria-describedby="verda-invoice-link-id-helper"
                />
                <FormHelperText>
                  <HelperText>
                    <HelperTextItem
                      id="verda-invoice-link-id-helper"
                      variant={
                        fieldErrors.facturier_invoice_id || (facturierId !== '' && !facturierIdValid)
                          ? 'error'
                          : 'default'
                      }
                    >
                      {fieldErrors.facturier_invoice_id ??
                        (facturierId !== '' && !facturierIdValid
                          ? 'A positive whole number.'
                          : 'The numeric id of the invoice in Facturier (not its numéro).')}
                    </HelperTextItem>
                  </HelperText>
                </FormHelperText>
              </FormGroup>
            </Form>
          </>
        );
      case 'release':
        return (
          <>
            <Alert variant="warning" isInline title="This can invoice the customer twice" className="pf-v6-u-mb-md">
              Release only after checking in Facturier that no invoice exists for this marker. Releasing DELETES this
              row and the month is invoiced again at the next run.
            </Alert>
            <Form onSubmit={(event) => event.preventDefault()}>
              <TypedConfirmField
                id="verda-invoice-release-confirm"
                expected={invoice.search_marker}
                value={typedMarker}
                onChange={setTypedMarker}
                hint="The search marker is typed out in full, after checking Facturier for it."
              />
              <NoteField
                id="verda-invoice-release-note"
                label="Note"
                value={note}
                onChange={setNote}
                error={fieldErrors.note ?? null}
                hint="What you checked in Facturier (kept as an INFO finding)."
              />
            </Form>
          </>
        );
      default:
        return (
          <>
            <Content component="p">
              Closing is <strong>terminal</strong>: the {invoice.hours} hour(s) of {formatMonthYear(invoice.period)}{' '}
              stay invoiced (never invoiced again) and can no longer be refunded here. Use it once the invoice was
              handled outside (credit note, manual invoice).
            </Content>
            <Form onSubmit={(event) => event.preventDefault()}>
              <NoteField
                id="verda-invoice-close-note"
                label="Note"
                value={note}
                onChange={setNote}
                error={fieldErrors.note ?? null}
                hint="How the invoice was handled outside."
              />
            </Form>
          </>
        );
    }
  };

  return (
    <Modal variant="small" isOpen onClose={busy ? undefined : onClose} aria-labelledby="verda-invoice-action-title">
      <ModalHeader
        title={TITLES[action]}
        labelId="verda-invoice-action-title"
        description={label}
        titleIconVariant={action === 'link' ? undefined : 'warning'}
      />
      <ModalBody>
        {error !== null && (
          <Alert variant="danger" isInline title={`Could not ${action} the invoice`} className="pf-v6-u-mb-md">
            {error}
          </Alert>
        )}
        {renderBody()}
      </ModalBody>
      <ModalFooter>
        <Button
          variant={action === 'link' ? 'primary' : 'danger'}
          onClick={() => void handleSubmit()}
          isLoading={busy}
          isDisabled={!canSubmit}
        >
          {action === 'link' ? 'Link invoice' : action === 'release' ? 'Release invoice' : 'Close invoice'}
        </Button>
        <Button variant="link" onClick={onClose} isDisabled={busy}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export default VerdaInvoiceActionModal;
